import React, { useCallback } from 'react';
import find from 'lodash/find';
import styled from 'styled-components';

import { AlertInfo } from '@ad-scripts/presentation/components/common/alert';
import { Button } from 'src/presentation/components/agent_desktop_components';
import { formatPhone } from 'src/actions/shared/phonesHelper';
import { getPublishedIvrs } from 'src/presentation/components/pages/shared/helpers/ivrsHelper';
import { H3 } from '@ad-scripts/presentation/components/common/headers';
import ivrsResource from 'src/actions/ivrs/ivrsResource';
import { PhoneNumberCapability } from 'src/models/CompanyPhoneNumber/CompanyPhoneNumber';
import {
  StepWizardActionContainer,
  StepWizardFieldWithBorder,
} from 'src/presentation/components/pages/email/components/StepWizardLayout';
import { StyledBold, StyledLink } from './shared/elements';
import { useResourceValues } from 'src/presentation/hooks/useResource';
import useSelectInbox from 'src/presentation/components/pages/channels/shared/hooks/useSelectInbox';

const getIvrName = (ivrs, ivrId) => {
  const { name, id, description } = find(ivrs, ({ id }: any) => id === ivrId) || {};
  return name || description || id;
};

const IvrTitle = ({ ivr, phoneNumber }) => {
  const { initialIvrName, queueIvrName, initialIvrId, queueIvrId } = ivr;
  const titleConfig = [
    {
      showTitle: initialIvrId && queueIvrId,
      title: (
        <>
          <StyledBold>{phoneNumber}</StyledBold> is active and linked to the initial IVR,{' '}
          <StyledBold>{initialIvrName}</StyledBold>, and queue IVR, <StyledBold>{queueIvrName}</StyledBold>.
        </>
      ),
    },
    {
      showTitle: initialIvrId && !queueIvrId,
      title: (
        <>
          <StyledBold>{phoneNumber}</StyledBold> is active and linked to the initial IVR,{' '}
          <StyledBold>{initialIvrName}</StyledBold>
        </>
      ),
    },
    {
      showTitle: !initialIvrId && queueIvrId,
      title: (
        <>
          <StyledBold>{phoneNumber}</StyledBold> is active and linked to the queue IVR,{' '}
          <StyledBold>{queueIvrName}</StyledBold>
        </>
      ),
    },
  ];
  const resultTitle = titleConfig.find(({ showTitle }) => showTitle)?.title || '';
  return <StyledDescription>{resultTitle}</StyledDescription>;
};

const RenderTemplate = ({ activateVoice, linkIvr, ivr, phoneNumber }) => {
  const { inboxName, editPhoneLink, inboxLink } = useSelectInbox(PhoneNumberCapability.VOICE, phoneNumber);

  const formattedPhoneNumber = formatPhone(phoneNumber);
  const config = [
    {
      show: activateVoice && linkIvr,
      template: (
        <>
          <IvrTitle ivr={ivr} phoneNumber={formattedPhoneNumber} />
          <StyledAlertInfo>
            <b>Tip:&nbsp;</b>
            <StyledLink
              target="_blank"
              rel="noopener noreferrer"
              href="https://help.gladly.com/implementation/docs/testing-checklist?highlight=ivr"
            >
              Test your configuration
            </StyledLink>{' '}
            to confirm that your IVR voice setup is working as intended.
          </StyledAlertInfo>
          <StyledInfo>
            Looking to customize your queue and hold music or select different IVRs? You can{' '}
            <StyledLink target="_blank" rel="noopener noreferrer" href={editPhoneLink}>
              edit this phone number
            </StyledLink>{' '}
            anytime.
          </StyledInfo>
        </>
      ),
    },
    {
      show: activateVoice,
      template: (
        <>
          <StyledDescription>
            <StyledBold>{formattedPhoneNumber}</StyledBold> is active and ready to use. You can always link an IVR to
            this number in the future.
          </StyledDescription>
          <StyledAlertInfo>
            <b>Tip:&nbsp;</b>
            Test your configuration! Go to the assigned Inbox for this number,{' '}
            <StyledLink target="_blank" rel="noopener noreferrer" href={inboxLink}>
              {inboxName}
            </StyledLink>
            , to confirm that your voice setup is working as intended.
          </StyledAlertInfo>
          <StyledInfo>
            Looking to customize your queue and hold music or link an IVR? You can{' '}
            <StyledLink target="_blank" rel="noopener noreferrer" href={editPhoneLink}>
              edit this phone number
            </StyledLink>{' '}
            anytime.
          </StyledInfo>
        </>
      ),
    },
    {
      show: activateVoice === false,
      template: (
        <>
          <StyledDescription>
            <StyledBold>{formattedPhoneNumber}</StyledBold> will remain inactive. Remember, you can always set up voice
            for this number in the future.
          </StyledDescription>
        </>
      ),
    },
  ];
  return config.find(({ show }) => show)?.template || <></>;
};

const SetupComplete = ({
  closeModal,
  phoneNumber,
  activateVoice,
  linkIvr,
  ivr,
  firstStep,
  setInitState,
}: {
  closeModal?;
  phoneNumber: string;
  ivr?;
  activateVoice: boolean;
  linkIvr: boolean;
  firstStep?;
  setInitState: () => void;
}): JSX.Element | null => {
  const { values: ivrConfigurations } = useResourceValues(ivrsResource);
  const ivrs = getPublishedIvrs(ivrConfigurations);
  const ivrWithName = {
    ...ivr,
    initialIvrName: getIvrName(ivrs, ivr.initialIvrId),
    queueIvrName: getIvrName(ivrs, ivr.queueIvrId),
  };
  const setUpAnotherNumberHandler = useCallback(() => {
    setInitState();
    firstStep();
  }, [setInitState, firstStep]);
  return (
    <>
      <StepWizardFieldWithBorder>
        <H3 data-aid="wizard-title">Setup complete</H3>
        <RenderTemplate activateVoice={activateVoice} linkIvr={linkIvr} ivr={ivrWithName} phoneNumber={phoneNumber} />
      </StepWizardFieldWithBorder>
      <StepWizardActionContainer>
        <Button data-aid="editor-done" type={Button.Types.PRIMARY} onClick={closeModal}>
          Done
        </Button>
        <Button data-aid="editor-save" type={Button.Types.SECONDARY} onClick={setUpAnotherNumberHandler}>
          Set up another number
        </Button>
      </StepWizardActionContainer>
    </>
  );
};

const StyledDescription = styled.p`
  line-height: 20px;
  margin-bottom: ${p => p.theme.spacing.insetMedium};
`;

const StyledInfo = styled.p`
  line-height: 20px;
  margin-bottom: 0;
`;

const StyledAlertInfo = styled(AlertInfo)`
  line-height: 20px;
  > svg {
    margin-top: 3px;
  }
`;

export default SetupComplete;
